import { z } from "zod";
import { StateMachine } from "../utils/state-machine.utils.js";
import { StatefulValue } from "../utils/stateful-value.utils.js";
import { InvalidEventStateError } from "../errors/event-state.error.js";

export const EventStatusSchema = z.enum({
  DRAFT: "DRAFT",
  PUBLISHED: "PUBLISHED",
  ONGOING: "ONGOING",
  ENDED: "ENDED",
  CANCELLED: "CANCELLED",
});

export type EventStatusType = z.infer<typeof EventStatusSchema>;

export class EventState extends StatefulValue<EventStatusType> {
  static readonly values = EventStatusSchema.enum;

  private static readonly TERMINAL_STATUSES: readonly EventStatusType[] = [
    "ENDED",
    "CANCELLED",
  ] as const;

  private static readonly TRANSITIONS_DATA: ReadonlyArray<
    readonly [EventStatusType, readonly EventStatusType[]]
  > = [
    ["DRAFT", ["PUBLISHED", "CANCELLED"]],
    ["PUBLISHED", ["ONGOING", "CANCELLED"]],
    ["ONGOING", ["ENDED", "CANCELLED"]],
    ["ENDED", []],
    ["CANCELLED", []],
  ];

  private static readonly stateMachine = new StateMachine(
    EventState.TRANSITIONS_DATA,
    EventState.TERMINAL_STATUSES,
  );

  static readonly DRAFT = new EventState(EventState.values.DRAFT);
  static readonly PUBLISHED = new EventState(EventState.values.PUBLISHED);
  static readonly ONGOING = new EventState(EventState.values.ONGOING);
  static readonly ENDED = new EventState(EventState.values.ENDED);
  static readonly CANCELLED = new EventState(EventState.values.CANCELLED);

  private constructor(protected readonly value: EventStatusType) {
    super(EventState.stateMachine, value);
  }

  static fromString(value: string): EventState {
    return StatefulValue.lookupOrThrow(
      EventState,
      value,
      (v) => new InvalidEventStateError(v),
    );
  }
}
